'use client';

import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/navigation';
import { type PointerEvent, useRef, useState } from 'react';
import { getSpartanRevealElementProps, SPARTAN_REVEAL_PRESET, SPARTAN_REVEAL_ROLE } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { SpartanLineupModelCardActions } from '@/app/lcv/components/sections/spartan/shared/lineup/carousel/model-card/actions';
import { SpartanLineupModelCardDarkSurface, SpartanLineupModelCardHoverBorder, SpartanLineupModelCardLightSurface } from '@/app/lcv/components/sections/spartan/shared/lineup/carousel/model-card/card-chrome';
import { SpartanLineupModelCardEngineDetails } from '@/app/lcv/components/sections/spartan/shared/lineup/carousel/model-card/engine-details';
import { SpartanLineupModelCardImageLink } from '@/app/lcv/components/sections/spartan/shared/lineup/carousel/model-card/model-card-image-link';
import { getSpartanLineupEngineTabsVariant } from '@/app/lcv/components/sections/spartan/shared/lineup/carousel/model-card/styles';
import type { SpartanLineupModelCardProps } from '@/app/lcv/components/sections/spartan/shared/lineup/carousel/model-card/types';
import styles from '@/app/lcv/components/sections/spartan/shared/lineup/styles.module.css';
import { SPARTAN_LINEUP_TRACKING } from '@/app/lcv/components/sections/spartan/shared/lineup/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
export const SpartanLineupModelCard = t0 => {
  const $ = _c(94);
  const {
    model,
    modelIndex,
    eventName,
    imageLoading: t1,
    tone: t2,
    styleName,
    isCarouselRevealed: t3,
    isGroupSwitchTransition: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? {
      loading: "lazy"
    } : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const imageLoading = t5;
  const tone = t2 === undefined ? "light" : t2;
  const isCarouselRevealed = t3 === undefined ? false : t3;
  const isGroupSwitchTransition = t4 === undefined ? false : t4;
  const router = useRouter();
  const prefetchedDetailHrefRef = useRef(null);
  const glowRef = useRef(null);
  const isDark = tone === "dark";
  let t6;
  if ($[2] !== styleName || $[3] !== tone) {
    t6 = getSpartanLineupEngineTabsVariant({
      tone,
      styleName
    });
    $[2] = styleName;
    $[3] = tone;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const engineTabsVariant = t6;
  const showLightModelImageBackground = styleName === "x-series" || engineTabsVariant === "filled" || engineTabsVariant === "soft" || engineTabsVariant === "light";
  const showFullCardHoverBorder = !isDark || styleName === "x-series";
  let t7;
  if ($[5] !== model.engines) {
    t7 = model.engines ?? [];
    $[5] = model.engines;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const engines = t7;
  const [selectedValue, setSelectedValue] = useState("0");
  const selectedIndex = Math.min(Math.max(0, Number(selectedValue) || 0), Math.max(0, engines.length - 1));
  const selectedEngine = engines[selectedIndex];
  const compareHref = selectedEngine?.compareHref ?? model.compareHref;
  let t8;
  if ($[7] !== model.href || $[8] !== router) {
    t8 = () => {
      if (prefetchedDetailHrefRef.current === model.href) {
        return;
      }
      prefetchedDetailHrefRef.current = model.href;
      router.prefetch(model.href);
    };
    $[7] = model.href;
    $[8] = router;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handlePrefetchDetail = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = value => {
      setSelectedValue(value);
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleSelectEngine = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = event => {
      if (event.pointerType !== "mouse" || !glowRef.current) {
        return;
      }
      const glowBounds = glowRef.current.getBoundingClientRect();
      event.currentTarget.style.setProperty("--spartan-lineup-glow-x", `${event.clientX - glowBounds.left}px`);
      event.currentTarget.style.setProperty("--spartan-lineup-glow-y", `${event.clientY - glowBounds.top}px`);
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const handlePointerMove = t10;
  const t11 = showLightModelImageBackground ? "gap-4" : "gap-1";
  const t12 = isGroupSwitchTransition && "is-inview";
  let t13;
  if ($[12] !== t11 || $[13] !== t12) {
    t13 = cn("group/model-card xs:basis-full relative flex max-w-94 min-w-0 shrink-0 grow-0 basis-[calc(100%-16px)] flex-col overflow-hidden", t11, styles.modelCardCarouselReveal, t12);
    $[12] = t11;
    $[13] = t12;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const t14 = isCarouselRevealed ? "" : undefined;
  let t15;
  if ($[15] !== modelIndex) {
    t15 = createDataAttributes({
      test: SPARTAN_LINEUP_TRACKING.model(modelIndex)
    });
    $[15] = modelIndex;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  const t16 = Math.min(modelIndex, 3);
  let t17;
  if ($[17] !== t16) {
    t17 = getSpartanRevealElementProps({
      order: t16,
      preset: SPARTAN_REVEAL_PRESET.collection,
      role: SPARTAN_REVEAL_ROLE.lineupCard,
      staggerMs: 60
    });
    $[17] = t16;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== isDark) {
    t18 = !isDark ? <SpartanLineupModelCardLightSurface /> : null;
    $[19] = isDark;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  const t19 = !isGroupSwitchTransition;
  let t20;
  if ($[21] !== eventName || $[22] !== handlePrefetchDetail || $[23] !== imageLoading || $[24] !== isDark || $[25] !== model || $[26] !== modelIndex || $[27] !== showLightModelImageBackground || $[28] !== t19) {
    t20 = <div className="aspect-16-9 relative z-20 overflow-hidden rounded-t-lg"><SpartanLineupModelCardImageLink model={model} modelIndex={modelIndex} eventName={eventName} imageLoading={imageLoading} showLightModelImageBackground={showLightModelImageBackground} isDark={isDark} isVehicleRevealEnabled={t19} onPrefetchDetail={handlePrefetchDetail} /></div>;
    $[21] = eventName;
    $[22] = handlePrefetchDetail;
    $[23] = imageLoading;
    $[24] = isDark;
    $[25] = model;
    $[26] = modelIndex;
    $[27] = showLightModelImageBackground;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== isDark || $[31] !== styleName || $[32] !== tone) {
    t21 = isDark ? <SpartanLineupModelCardDarkSurface tone={tone} styleName={styleName} /> : null;
    $[30] = isDark;
    $[31] = styleName;
    $[32] = tone;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== showFullCardHoverBorder || $[35] !== styleName || $[36] !== tone) {
    t22 = !showFullCardHoverBorder ? <SpartanLineupModelCardHoverBorder ref={glowRef} tone={tone} styleName={styleName} /> : null;
    $[34] = showFullCardHoverBorder;
    $[35] = styleName;
    $[36] = tone;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const t23 = isDark ? "text-white" : "text-gray-900";
  let t24;
  if ($[38] !== t23) {
    t24 = cn("font-display text-h2 xs:text-[40px]", t23);
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== model.title) {
    t25 = {
      __html: model.title
    };
    $[40] = model.title;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== modelIndex) {
    t26 = createDataAttributes({
      test: SPARTAN_LINEUP_TRACKING.modelTitle(modelIndex)
    });
    $[42] = modelIndex;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== t24 || $[45] !== t25 || $[46] !== t26) {
    t27 = <h3 className={t24} dangerouslySetInnerHTML={t25} {...t26} />;
    $[44] = t24;
    $[45] = t25;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== isDark || $[49] !== model.startPrice || $[50] !== modelIndex) {
    t28 = !!model.startPrice && <div className={cn("text-body-2 where-span:text-body-1 where-span:font-noto-bold py-2", isDark ? "where-span:text-white text-gray-200" : "where-span:text-gray-900 text-gray-600")} dangerouslySetInnerHTML={{
      __html: model.startPrice
    }} {...createDataAttributes({
      test: SPARTAN_LINEUP_TRACKING.modelStartPrice(modelIndex)
    })} />;
    $[48] = isDark;
    $[49] = model.startPrice;
    $[50] = modelIndex;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t27 || $[53] !== t28) {
    t29 = <div>{t27}{t28}</div>;
    $[52] = t27;
    $[53] = t28;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== engines || $[56] !== eventName || $[57] !== isDark || $[58] !== model.engineRemark || $[59] !== model.engineTabsTitle || $[60] !== model.title || $[61] !== modelIndex || $[62] !== selectedEngine || $[63] !== selectedIndex || $[64] !== styleName) {
    t30 = !!selectedEngine && <SpartanLineupModelCardEngineDetails engines={engines} modelTitle={model.title} selectedEngine={selectedEngine} selectedIndex={selectedIndex} modelIndex={modelIndex} eventName={eventName} isDark={isDark} styleName={styleName} engineTabsTitle={model.engineTabsTitle} engineRemark={model.engineRemark} onSelectEngine={handleSelectEngine} />;
    $[55] = engines;
    $[56] = eventName;
    $[57] = isDark;
    $[58] = model.engineRemark;
    $[59] = model.engineTabsTitle;
    $[60] = model.title;
    $[61] = modelIndex;
    $[62] = selectedEngine;
    $[63] = selectedIndex;
    $[64] = styleName;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== compareHref || $[67] !== eventName || $[68] !== handlePrefetchDetail || $[69] !== isDark || $[70] !== model.href || $[71] !== modelIndex) {
    t31 = <SpartanLineupModelCardActions modelHref={model.href} compareHref={compareHref} modelIndex={modelIndex} eventName={eventName} isDark={isDark} onPrefetchDetail={handlePrefetchDetail} />;
    $[66] = compareHref;
    $[67] = eventName;
    $[68] = handlePrefetchDetail;
    $[69] = isDark;
    $[70] = model.href;
    $[71] = modelIndex;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== t29 || $[74] !== t30 || $[75] !== t31) {
    t32 = <div className="relative z-10 flex flex-1 flex-col">{t29}{t30}{t31}</div>;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== t21 || $[78] !== t22 || $[79] !== t32) {
    t33 = <div className="relative z-10 flex flex-1 flex-col px-4 pt-0 pb-4 md:px-6">{t21}{t22}{t32}</div>;
    $[77] = t21;
    $[78] = t22;
    $[79] = t32;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== showFullCardHoverBorder || $[82] !== styleName || $[83] !== tone) {
    t34 = showFullCardHoverBorder ? <SpartanLineupModelCardHoverBorder ref={glowRef} tone={tone} styleName={styleName} /> : null;
    $[81] = showFullCardHoverBorder;
    $[82] = styleName;
    $[83] = tone;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== t13 || $[86] !== t14 || $[87] !== t15 || $[88] !== t17 || $[89] !== t18 || $[90] !== t20 || $[91] !== t33 || $[92] !== t34) {
    t35 = <li className={t13} data-spartan-lineup-carousel-revealed={t14} onPointerMove={handlePointerMove} {...t15} {...t17}>{t18}{t20}{t33}{t34}</li>;
    $[85] = t13;
    $[86] = t14;
    $[87] = t15;
    $[88] = t17;
    $[89] = t18;
    $[90] = t20;
    $[91] = t33;
    $[92] = t34;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  return t35;
};